// =====================================================================
// EGL v12 Frontend - PUBLIC CERTIFICATE VIEW
// Purpose: Display public certificate information with professional design
// =====================================================================

import React, { useState, useEffect } from 'react'
import { PublicCertificateResponse } from '../types/certificate'
import { certificateService } from '../services/certificateService'

interface PublicCertificateViewProps {
  certificateUuid: string
}

interface PublicCertificateData {
  cert_id: string;
  uurlid: string;
  gemstones: Array<{
    gemstone_type: string;
    abbreviation: string;
    description?: string;
    display_order: number;
    carat_weight: number;
    measurement_type: string;
    measurement_value: number | null;
    measurement_unit: string;
    measurement_range: string | null;
    growth_origin: string;
    growth_abbreviation: string;
    diamond_color_type: string | null;
    diamond_color_fixed: string | null;
    diamond_color_range: string | null;
    diamond_clarity_type: string | null;
    diamond_clarity_fixed: string | null;
    diamond_clarity_range: string | null;
  }>;
  gemstone_gross_weight: number;
  gemstone_growth_origin: string;
  metal_composition: string;
  item_gross_weight: number;
  card_design: string;
  image_url?: string;
  image_filename?: string;
  status: string;
  validation_status: string;
  published_at?: string;
  comment_lines: string[];
  abbreviation_lines: string[];
}

export const PublicCertificateView: React.FC<PublicCertificateViewProps> = ({ certificateUuid }) => {
  const [certificate, setCertificate] = useState<PublicCertificateData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Mouse tracking for interactive background
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth) * 100
      const y = (e.clientY / window.innerHeight) * 100
      document.documentElement.style.setProperty('--mouse-x', `${x}%`)
      document.documentElement.style.setProperty('--mouse-y', `${y}%`)
    }

    document.addEventListener('mousemove', handleMouseMove)
    return () => document.removeEventListener('mousemove', handleMouseMove)
  }, [])

  useEffect(() => {
    const loadCertificate = async () => {
      try {
        setLoading(true)
        const result = await certificateService.getPublicCertificate(certificateUuid)
        
        if (result.success && result.data) {
          setCertificate(result.data.certificate as unknown as PublicCertificateData)
        } else {
          setError(result.message || 'Certificate not found')
        }
      } catch (err) {
        setError('Failed to load certificate')
        console.error('Certificate loading error:', err)
      } finally {
        setLoading(false)
      }
    }

    if (certificateUuid) {
      loadCertificate()
    }
  }, [certificateUuid])

  // Helper functions removed since we're only displaying specific fields now

  const currentYear = new Date().getFullYear()

  if (loading) {
    return (
      <div className="container">
        <div className="section">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="spinner"></div>
            <p>Loading certificate...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !certificate) {
    return (
      <div className="container">
        <div className="section">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h2>Certificate Not Found</h2>
            <p>{error || 'The requested certificate could not be found.'}</p>
            <p>Please check the certificate URL and try again.</p>
          </div>
        </div>
      </div>
    )
  }

  // Remove unused color/clarity extraction since we're not displaying these fields

  // Filter comment lines to only show those with data (handle both null and empty strings)
  const validCommentLines = certificate.comment_lines.filter(comment => comment && comment.trim() !== '')
  
  // Filter abbreviation lines to show all that have data (handle both null and empty strings)
  const validAbbreviationLines = certificate.abbreviation_lines
    .filter(abbr => abbr && abbr.trim() !== '')

  return (
    <div className="container">
      {/* Logo Section */}
      <div className="logo section">
        <img src="https://verifygold.s3.me-central-1.amazonaws.com/EGL/Logo/EGL_Logo.svg" alt="EGL Logo" />
      </div>
      
      {/* CERTIFICATE ID */}
      {certificate.cert_id && (
        <div className="section field">
          <strong>CERTIFICATE ID</strong>
          <span>{certificate.cert_id}</span>
        </div>
      )}
      
      {/* ITEM GROSS WEIGHT */}
      {certificate.item_gross_weight && (
        <div className="section field">
          <strong>ITEM GROSS WEIGHT</strong>
          <span>{Number(certificate.item_gross_weight).toFixed(2)} GRAMS</span>
        </div>
      )}
      
      {/* METAL COMPOSITION */}
      {certificate.metal_composition && (
        <div className="section field">
          <strong>METAL COMPOSITION</strong>
          <span>{certificate.metal_composition}</span>
        </div>
      )}
      
      {/* GEMSTONE GROWTH ORIGINS */}
      {certificate.gemstone_growth_origin && (
        <div className="section field">
          <strong>GEMSTONE GROWTH ORIGINS</strong>
          <span>{certificate.gemstone_growth_origin}</span>
        </div>
      )}
      
      {/* GEMSTONE SPECIFICATIONS - From gemstones array or comment lines */}
      {certificate.gemstones && certificate.gemstones.length > 0 ? (
        <div className="section gemstone-specifications">
          <strong>GEMSTONE SPECIFICATIONS</strong>
          <div className="gemstone-specs-content">
            {certificate.gemstones.map((gemstone, index) => {
              const specs = [];
              
              // Format gemstone type: Remove _1, _2, _3 suffixes and convert to proper case
              let gemType = gemstone.gemstone_type.replace(/_\d+$/, ''); // Remove _1, _2, _3
              gemType = gemType.replace(/_/g, ' '); // Replace underscores with spaces
              // Convert to proper case (first letter uppercase, rest lowercase)
              gemType = gemType.split(' ').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
              ).join(' ');
              specs.push(gemType);
              
              // Check if this is a pearl (measurement_unit = 'mm')
              const isPearl = gemstone.measurement_unit === 'mm';
              
              if (isPearl) {
                // Pearl measurements (in MM)
                if (gemstone.measurement_range) {
                  specs.push(`${gemstone.measurement_range} MM`);
                } else if (gemstone.measurement_value) {
                  const diameter = parseFloat(gemstone.measurement_value.toString()).toFixed(2);
                  specs.push(`${diameter} MM`);
                }
              } else {
                // Traditional gemstone (in carats)
                if (gemstone.carat_weight) {
                  const weight = parseFloat(gemstone.carat_weight.toString()).toFixed(2);
                  specs.push(`${weight} Carats`);
                }
                if (gemstone.growth_origin) {
                  // Convert growth origin to proper case
                  const origin = gemstone.growth_origin.split(' ').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                  ).join(' ');
                  specs.push(origin);
                }
              }
              
              // Diamond-specific fields
              if (gemstone.diamond_color_range) specs.push(`Color: ${gemstone.diamond_color_range}`);
              if (gemstone.diamond_color_fixed) specs.push(`Color: ${gemstone.diamond_color_fixed}`);
              if (gemstone.diamond_clarity_range) specs.push(`Clarity: ${gemstone.diamond_clarity_range}`);
              if (gemstone.diamond_clarity_fixed) specs.push(`Clarity: ${gemstone.diamond_clarity_fixed}`);
              
              return (
                <div key={index} className="gemstone-spec-line">
                  {index + 1}. {specs.join(', ')}
                </div>
              );
            })}
          </div>
        </div>
      ) : validCommentLines.length > 0 ? (
        <div className="section gemstone-specifications">
          <strong>GEMSTONE SPECIFICATIONS</strong>
          <div className="gemstone-specs-content">
            {validCommentLines.map((comment, index) => (
              <div key={index} className="gemstone-spec-line">{comment}</div>
            ))}
          </div>
        </div>
      ) : null}
      
      {/* Authentication & Grading Info Block */}
      <div className="info-block section">
        <strong>Authentication & Grading:</strong>
        Authentication, grading, and origin were determined using non-invasive methods, as permitted by the item's mounting. Abbreviations marked with * are defined below.
      </div>
      
      {/* Image Section */}
      <div className="image-box section">
        {certificate.image_url ? (
          <img src={certificate.image_url} alt="Gemstone" />
        ) : (
          <div className="no-image">
            <span>Image not available</span>
          </div>
        )}
      </div>
      
      {/* Abbreviations Section - Only first 3 lines with data */}
      {validAbbreviationLines.length > 0 && (
        <div className="abbreviations section">
          <h3>ABBREVIATION(S)</h3>
          <div className="abbreviation-lines">
            {validAbbreviationLines.map((abbreviation, index) => (
              <div key={index}>{abbreviation}</div>
            ))}
          </div>
        </div>
      )}
      
      {/* Report Conditions Info Block */}
      <div className="info-block section">
        <strong>Report Conditions:</strong>
        This report is not a guarantee, valuation, or appraisal. It reflects the characteristics of the article at the time of examination using methods and equipment available to EUROPEAN GEMOLOGICAL LAB LTD.
        Results may vary with different examiners, conditions, or technological advances.
        EUROPEAN GEMOLOGICAL LAB LTD accepts no liability for errors, omissions, or use of this report or any inscription, including indirect or consequential damages.
        Only the original report, accessible via the QR CODE on the front of the original card certificate, reflects the most current grading information. PDF, shared, or printed copies are not authoritative.
      </div>
      
      {/* Footer */}
      <div className="footer section">
        71–75 Shelton Street, Covent Garden<br />
        London WC2H 9JQ, England, United Kingdom (UK)<br />
        &copy; {currentYear} European Gemological Lab Ltd. | <a href="https://www.eugemlab.com">www.eugemlab.com</a>
      </div>
    </div>
  )
}

// Component for use with React Router
export const PublicCertificatePage: React.FC = () => {
  // Extract UUID from URL path
  const pathParts = window.location.pathname.split('/')
  const certificateUuid = pathParts[pathParts.length - 1]

  if (!certificateUuid) {
    return (
      <div className="container">
        <div className="section">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h2>Invalid Certificate URL</h2>
            <p>Please check the certificate URL and try again.</p>
          </div>
        </div>
      </div>
    )
  }

  return <PublicCertificateView certificateUuid={certificateUuid} />
} 